<template>
  <div>
    <BoxComponent :mb="24">
      <FlexComponent align="center" justify="space-between" :gap="8" wrap="wrap">
        <search-box placeholder="Search product" name="linksSearch" :on-change="changeSearchQuery" class="links-table__search-box" />
        <ButtonComponent variant="accent" class="links-table__export-btn" @click="exportLinkStats">
          <template #iconLeft>
            <el-icon :size="18">
              <DownloadIcon />
            </el-icon>
          </template>
          Export Stats
        </ButtonComponent>
      </FlexComponent>
    </BoxComponent>
    <Table :pagination="pagination" :pages-with-data="pagesWithData" :loading="loading" :change-page="changePage">
      <template #thead>
        <tr>
          <TableHeader label="Product" column-attribute="title" :sort-by="sortBy" :sort-order="sortOrder" :on-update-sort="changeSort" />
          <TableHeader
            label="Creator"
            align="left"
            column-attribute="username"
            :sort-by="sortBy"
            :sort-order="sortOrder"
            :on-update-sort="changeSort"
          />
          <TableHeader
            v-if="!hideClicks"
            label="Clicks"
            column-attribute="clicks"
            align="right"
            :sort-by="sortBy"
            :sort-order="sortOrder"
            :on-update-sort="changeSort"
          />
          <TableHeader
            v-if="!hideCosts"
            label="Est. cost"
            column-attribute="cost"
            align="right"
            :sort-by="sortBy"
            :sort-order="sortOrder"
            :on-update-sort="changeSort"
          />
          <TableHeader
            v-if="!hideCosts"
            label="Avg. cost/click"
            column-attribute="cost_per_click"
            align="right"
            :sort-by="sortBy"
            :sort-order="sortOrder"
            :on-update-sort="changeSort"
          />
          <TableHeader
            v-if="!hideSales"
            label="Order value"
            column-attribute="order_value"
            align="right"
            :sort-by="sortBy"
            :sort-order="sortOrder"
            :on-update-sort="changeSort"
          />
          <TableHeader v-if="!(hideSales || hideCosts)" label="ROAS" align="right" />
          <TableHeader
            v-if="!hideSales"
            label="Nr orders"
            column-attribute="nr_orders"
            align="right"
            :sort-by="sortBy"
            :sort-order="sortOrder"
            :on-update-sort="changeSort"
          />
          <TableHeader
            label="Created at"
            column-attribute="date"
            align="right"
            :sort-by="sortBy"
            :sort-order="sortOrder"
            :on-update-sort="changeSort"
          />
        </tr>
      </template>
      <template #tbody>
        <tr v-for="(link, key) in pagesWithData[pagination.currentPage]" :key="key">
          <td class="align-left">
            <FlexComponent align="center" justify="flex-end" :gap="8" wrap="wrap"> </FlexComponent>
            <div class="d-flex align-items-center gap-2">
              <ImageComponent
                v-if="link.image"
                class="link-image"
                :image="link.image"
                :width="48"
                :height="48"
                :alt="link.title || link.text"
              />
              <FlexComponent align="flex-start" direction="column">
                <TypographyComponent variant="label-large" class="links-table__creator-name">
                  {{ link.title || link.text }}
                </TypographyComponent>
                <a class="link-href" :href="link.originalUrl" :title="link.originalUrl" target="_blank">
                  <FlexComponent wrap="nowrap" align="center">
                    <el-icon :size="16" class="el-icon--left">
                      <LinkIcon />
                    </el-icon>
                    <TypographyComponent variant="body-small" class="white-space-nowrap"> Link to product </TypographyComponent>
                  </FlexComponent>
                </a>
              </FlexComponent>
            </div>
          </td>
          <td class="align-left">
            <div class="links-table__creator-name">{{ link.username }}</div>
          </td>
          <td v-if="!hideClicks" class="align-right">{{ formatNumber(link.clicks) }}</td>
          <td v-if="!hideCosts" class="align-right">
            {{ formatMoney(link.cost) }}
          </td>
          <td v-if="!hideCosts" class="align-right">
            {{ formatMoney(link.costPerClick) }}
          </td>
          <td v-if="!hideSales" class="align-right">
            {{ formatMoney(link.orderValue) }}
          </td>
          <td v-if="!(hideSales || hideCosts)" class="align-right">
            {{ link.cost ? Math.round((link.orderValue / link.cost) * 100) / 100 : '-' }}
          </td>
          <td v-if="!hideSales" class="align-right">{{ formatNumber(link.nrOfOrders) }}</td>
          <td class="align-right">
            <div>{{ formatDateOnly(link.createdAt) }}</div>
            <div>{{ formatTimeOnly(link.createdAt) }}</div>
          </td>
        </tr>
      </template>
    </Table>
  </div>
</template>

<script lang="ts">
import { ButtonComponent } from '@metapic-apps/shared'
import { mapActions, mapGetters } from 'vuex'

import DownloadIcon from '@/assets/icons/DownloadStoryIcon.svg?component'
import LinkIcon from '@/assets/icons/link.svg?component'
import Table from '@/components/table/Table.vue'
import TableHeader from '@/components/table/TableHeader.vue'
import storeRepository from '@/legacy/repositories/storeRepository'
import { formatMoney, formatNumber } from '@/legacy/store/helpFunctions'
import { formatDateOnly, formatTimeOnly } from '@/lib/formatters'
import BoxComponent from '@/ui-library/atoms/BoxComponent/BoxComponent.vue'
import FlexComponent from '@/ui-library/atoms/FlexComponent/FlexComponent.vue'
import ImageComponent from '@/ui-library/atoms/ImageComponent.vue'
import TypographyComponent from '@/ui-library/atoms/TypographyComponent/TypographyComponent.vue'

export default {
  name: 'LinksTable',
  components: {
    TypographyComponent,
    FlexComponent,
    DownloadIcon,
    BoxComponent,
    ButtonComponent,
    // eslint-disable-next-line vue/no-reserved-component-names
    Table,
    TableHeader,
    ImageComponent,
    LinkIcon,
  },
  computed: {
    ...mapGetters('user', ['user', 'dataDateRange', 'token']),
    ...mapGetters('shop', ['shop', 'hideClicks', 'hideCosts', 'hideSales']),
    ...mapGetters('links', ['pagesWithData', 'loading', 'sortBy', 'sortOrder', 'searchQuery', 'pagination']),
  },
  mounted() {
    this.initModule()
  },
  methods: {
    formatDateOnly,
    formatTimeOnly,
    ...mapActions('links', ['changePage', 'changeSort', 'changeSearchQuery', 'initModule']),
    formatMoney,
    formatNumber,
    async exportLinkStats() {
      await storeRepository
        .getLinkStats(
          this.token,
          this.shop.id,
          { from: this.dataDateRange.start, to: this.dataDateRange.end },
          1,
          this.sortBy,
          this.sortOrder,
          this.searchQuery,
          [],
          null,
          true,
        )
        .then(({ data }) => {
          const blob = new Blob([data], {
            type: 'application/vnd.ms-excel',
          })
          const link = document.createElement('a')
          link.href = URL.createObjectURL(blob)
          link.download = 'link-stats.xlsx'
          link.click()
          URL.revokeObjectURL(link.href)
        })
    },
  },
}
</script>

<style scoped>
.links-table__search-box {
  width: 320px;
}
.links-table__creator-name {
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  max-width: 150px;
}
.link-image {
  border-radius: 8px;
}
.link-href {
  display: flex;
  align-items: center;
}
</style>
